import Header from "../../../layouts/Header"
import { styled, createTheme, ThemeProvider } from "@mui/material/styles"
import {
  Box,
  Button,
  FormControl,
  FormControlLabel,
  RadioGroup,
  Radio,
  FormLabel,
  Modal,
  IconButton,
  List,
  ListItem,
  Divider,
  ListItemText,
  Accordion,
  AccordionDetails,
  Typography,
  AccordionSummary,
} from "@mui/material"

import BackIcon from "../../../components/BackIcon"
import CustomTextFieldReadable from "../../../components/CustomTextFieldReadable"
import React, { useEffect, useState, useRef, forwardRef, useImperativeHandle } from "react"
import CustomFieldset from "../../../components/CustomFieldset"
import CustomTextField from "../../../components/CustomTextField"
import CustomDatePicker from "../../../components/CustomDatePicker"
import dayjs from "dayjs"
import fetchwrapper from "../../../services/interceptors/fetchwrapper"
import { useQuery, useMutation } from "@tanstack/react-query"
import CustomBackdrop from "../../../components/CustomBackdrop"
import CustomTooltip from "../../../components/ToolTip"
import GrabarIcon from "../../../assets/iconos/Grabar.ico"
import { useTransition, animated } from "@react-spring/web"
import CustomHelperDetail from "../../../components/CustomHelperDetail"
import CustomHelper from "../../../components/CustomHelper"
import CustomAutocomplete from "../../../components/CustomAutocomplete"
import CustomSingleRowSelectionServerSide from "../../../components/CustomSingleRowSelectionServerSide"
import CloseIcon from "@mui/icons-material/Close"
import DeleteIcon from "@mui/icons-material/Delete"
import ExpandMoreIcon from "@mui/icons-material/ExpandMore"
import normalFormatDate from "../../utils/date/DDMMYYYFormatDate"
import { useQuery as CustomUseQuery, notificationService } from "../../../api"
import { useNavigate } from "react-router-dom"

function capitalize(str) {
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase()
}

const StyledRoot = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  marginTop: theme.spacing(8),
  flexGrow: 1,
  padding: "0 16px",
  height: "100vh",
}))

const theme = createTheme({
  palette: {
    primary: {
      main: "#196C87",
    },
    secondary: {
      main: "#196C87", // Cambia el color secundario a verde azulado
    },
  },
})

const CITAS_TYPES = {
  COUNTER: "counter",
  TRATAMIENTO: "tratamiento",
  AGENDA: "agenda",
}

// ************************************************************
//                 FloatingAccordionLike
// ************************************************************
/**
 * Un contenedor que ocupa un lugar fijo en el formulario.
 * Al hacer clic en el "resumen", despliega/oculta un panel flotante
 * con scroll, sin empujar el contenido de abajo.
 *
 * @param {Array}   items - Lista de registros ({ id, name, description, ... }).
 * @param {Function} onRemoveItem - Función que recibe un registro para eliminarlo.
 * @param {string}  title - Título o texto del "resumen".
 * @param {number}  maxHeight - Altura máxima del panel flotante.
 */
const FloatingAccordionLike = ({ items, onRemoveItem, title = "Ver / Ocultar Lista", maxHeight = 200 }) => {
  const [open, setOpen] = useState(false)
  const containerRef = useRef(null)

  const handleToggle = () => {
    setOpen((prev) => !prev)
  }

  // Detector de clics fuera del componente
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setOpen(false)
      }
    }

    if (open) {
      document.addEventListener("click", handleClickOutside)
    }

    return () => {
      document.removeEventListener("click", handleClickOutside)
    }
  }, [open])

  return (
    <Box
      ref={containerRef}
      sx={{
        // Este Box está en flujo normal; ocupa su espacio en el formulario
        // Ajusta el alto y ancho según tu diseño
        border: "1px solid #ccc",
        borderRadius: 1,
        backgroundColor: "#f5f5f5",
        position: "relative", // Para que el panel flotante sea "absolute" respecto a este contenedor
        p: 1,
      }}
    >
      {/* "Resumen" estilo accordion */}
      <Box
        onClick={handleToggle}
        sx={{
          display: "flex",
          alignItems: "center",
          cursor: "pointer",
          userSelect: "none",
        }}
      >
        <Typography variant="subtitle1" sx={{ flexGrow: 1 }}>
          {title}
        </Typography>
        <ExpandMoreIcon
          sx={{
            transform: open ? "rotate(180deg)" : "rotate(0deg)",
            transition: "transform 0.3s",
          }}
        />
      </Box>

      {/* Panel flotante que se superpone sin empujar el contenido */}
      {open && (
        <Box
          sx={{
            position: "absolute",
            top: "50px", // justo debajo del "resumen" (ajusta según tu preferencia)
            left: 0,
            width: "100%",
            maxHeight,
            overflowY: "auto",
            backgroundColor: "#fff",
            border: "1px solid #ccc",
            borderRadius: 1,
            boxShadow: 3,
            zIndex: 9999, // Para que se muestre por encima de otros elementos
          }}
        >
          <List>
            {items.map((item, index) => (
              <React.Fragment key={index}>
                <ListItem
                  secondaryAction={
                    <IconButton edge="end" aria-label="delete" onClick={() => onRemoveItem(item)}>
                      <DeleteIcon sx={{ color: "#F92C43" }} />
                    </IconButton>
                  }
                >
                  <ListItemText primary={item?.artdescri} />
                  <ListItemText secondary={`Sesión #${item?.sessecuen}`} />
                </ListItem>
                {index < items.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>
        </Box>
      )}
    </Box>
  )
}

// ************************************************************
//                 PANTALLA CITA COUNTER
// ************************************************************
const ContainerFormCounter = styled(Box)(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "repeat(12, 1fr)",
  gridTemplateRows: "auto auto auto",
  gridTemplateAreas: `
          "IPedidoID IPedidoID IPedidoID IPedidoID IPedidoID IPedidoID IVendedor IVendedor IVendedor IVendedor IVendedor IVendedor"
          "IFechaInicial IFechaInicial IFechaInicial IFechaInicial IFechaInicial IFechaInicial IFechaFinal IFechaFinal IFechaFinal IFechaFinal IFechaFinal IFechaFinal"
          "ICliente ICliente ICliente ICliente ICliente ICliente ICliente ICliente ICliente ICliente ICliente ICliente"
          "IComentario IComentario IComentario IComentario IComentario IComentario IComentario IComentario IComentario IComentario IComentario IComentario"
        `,
  gap: "8px",
  alignItems: "center",
  // Versión para pantallas pequeñas
  [theme.breakpoints.down("md")]: {
    gridTemplateColumns: "1fr",
    gridTemplateRows: "auto auto auto auto auto auto",
    gridTemplateAreas: `
      "IPedidoID"
      "IVendedor"
      "IFechaInicial"
      "IFechaFinal"
      "ICliente"
      "IComentario"
    `,
    gap: "12px",
  },
}))

const IPedidoIDCounter = styled(Box)({
  gridArea: "IPedidoID",
})
const IVendedorCounter = styled(Box)({
  gridArea: "IVendedor",
})
const IFechaInicialCounter = styled(Box)({
  gridArea: "IFechaInicial",
})
const IFechaFinalCounter = styled(Box)({
  gridArea: "IFechaFinal",
})
const IClienteCounter = styled(Box)({
  gridArea: "ICliente",
})
const IComentarioCounter = styled(Box)({
  gridArea: "IComentario",
})

const style = {
  Modal: {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: "98%",
    minHeight: "98%",
    maxHeight: "98%",
    bgcolor: "background.paper",
    border: "2px solid #000",
    boxShadow: 24,
    p: 4,
    overflowY: "auto",
  },

  CloseIcon: {
    cursor: "pointer",
    marginLeft: "95%",
  },
}
const PantallaCitaCounter = forwardRef(({ dataInfoPednumped, dataInfoVendedor, refetchGetAllInfoFormulario }, ref) => {
  const [formData, setFormData] = useState({
    pednumped: "",
    vendedor: null, // Manaje el estado del autocomplete
    vennombre: "",
    vencodigo: "",
    pedfecemi: dayjs(new Date()),
    pedfecven: dayjs(new Date()),
    nickname: "",
    comentario: "",
  })
  const handleSetFormData = (k, v) => setFormData((prev) => ({ ...prev, [k]: v }))

  useEffect(() => {
    if (dataInfoPednumped) {
      setFormData((prev) => ({
        ...prev,
        pednumped: dataInfoPednumped,
      }))
    }
  }, [dataInfoPednumped])

  const { mutateAsync: savePedido, isPending: isSavingPedido } = useSavePedido()

  function useSavePedido() {
    return useMutation({
      mutationFn: async (data) => {
        const options = {
          method: "POST",
          body: JSON.stringify(data),
          headers: {
            "Content-Type": "application/json",
            "Access-Control-Allow-Origin": "*",
          },
        }

        let response = await fetchwrapper(`/AdministradorPedidosMostrador/saveCitaCounter`, options)

        response = response.json()
        return response
      },
      onError: () => {
        alert("No se pudo hacer el pedido. Intente otra vez")
      },
      onSuccess: () => {
        alert("Pedido realizada con éxito")
      },
    })
  }

  const handleCreating = async () => {
    if (!formData.pedfecemi.isValid()) {
      return alert("Digite una fecha de emisión válida")
    }
    if (!formData.pedfecven.isValid()) {
      return alert("Digite una fecha de vencimiento válida")
    }

    if (!formData.vendedor || Object.keys(formData.vendedor || {}).length === 0) {
      return alert("Digite un vendedor")
    }

    if (!formData.nickname || formData.nickname === "") {
      return alert("Digite un nombre")
    }

    await savePedido({
      pednumped: formData.pednumped,
      vennombre: formData.vennombre,
      vencodigo: formData.vencodigo,
      pedfecemi: dayjs(new Date()),
      pedfecven: dayjs(new Date()),
      nickname: formData.nickname.trim(),
      comentario: formData.comentario.trim(),
    })

    // Limpiar pantalla
    setFormData({
      pednumped: "",
      vennombre: "",
      vencodigo: "",
      pedfecemi: dayjs(new Date()),
      pedfecven: dayjs(new Date()),
      clinombre: "",
      nickname: "",
      comentario: "",
    })

    refetchGetAllInfoFormulario()
  }

  // Expone la función al padre
  useImperativeHandle(ref, () => ({
    save: handleCreating,
  }))

  return (
    <>
      <CustomBackdrop isLoading={isSavingPedido} />
      <ContainerFormCounter>
        <IPedidoIDCounter>
          <CustomTextFieldReadable
            label="Pedido ID"
            value={formData.pednumped}
            sxInput={{
              backgroundColor: "#E0CB79",
            }}
          ></CustomTextFieldReadable>
        </IPedidoIDCounter>
        <IVendedorCounter>
          <CustomAutocomplete
            label="Vendedor"
            selectedOption={formData.vendedor}
            setSelectedOption={(v) => {
              setFormData((prev) => ({
                ...prev,
                vendedor: v,
                vencodigo: v?.vencodigo ?? null,
                vennombre: v?.vennombre ?? null,
              }))
            }}
            options={dataInfoVendedor}
          />
        </IVendedorCounter>
        <IFechaInicialCounter>
          <CustomDatePicker
            label="Fecha Emisión"
            value={formData.pedfecemi}
            setValue={(v) => handleSetFormData("pedfecemi", v)}
            slotProps={{ textField: { fullWidth: true } }}
            disabled
          />
        </IFechaInicialCounter>
        <IFechaFinalCounter>
          <CustomDatePicker
            label="Fecha Vencimiento"
            value={formData.pedfecven}
            setValue={(v) => handleSetFormData("pedfecven", v)}
            slotProps={{ textField: { fullWidth: true } }}
            disabled
          />
        </IFechaFinalCounter>

        <IClienteCounter>
          <CustomTextField
            label="Nickname"
            value={formData.nickname}
            setValue={(v) => handleSetFormData("nickname", v)}
          ></CustomTextField>
        </IClienteCounter>

        <IComentarioCounter>
          <CustomTextField
            label="Comentario"
            value={formData.comentario}
            setValue={(v) => handleSetFormData("comentario", v)}
          />
        </IComentarioCounter>
      </ContainerFormCounter>
    </>
  )
})

// ************************************************************
//                 PANTALLA CITA TRATAMIENTO
// ************************************************************

const ContainerFormTratamiento = styled(Box)(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "repeat(12, 1fr)",
  gridTemplateRows: "auto auto auto",
  gridTemplateAreas: `
          "IPedidoID IPedidoID IPedidoID IPedidoID ICliente ICliente ICliente ICliente ICliente ICliente ICliente ICliente"
          "IServicioBtn IServicioBtn IServicioBtn IServicioAcc IServicioAcc IServicioAcc IServicioAcc IServicioAcc IServicioAcc IServicioAcc IServicioAcc IServicioAcc"
          "INickname INickname INickname INickname INickname INickname IVendedor IVendedor IVendedor IVendedor IVendedor IVendedor"
          "IFechaInicial IFechaInicial IFechaInicial IFechaInicial IFechaInicial IFechaInicial IFechaFinal IFechaFinal IFechaFinal IFechaFinal IFechaFinal IFechaFinal"
          "IComentario IComentario IComentario IComentario IComentario IComentario IComentario IComentario IComentario IComentario IComentario IComentario"
        `,
  gap: "8px",
  alignItems: "center",

  // Versión para pantallas pequeñas
  [theme.breakpoints.down("md")]: {
    gridTemplateColumns: "repeat(12, 1fr)",
    gridTemplateRows: "auto auto auto auto auto auto auto auto",
    gridTemplateAreas: `
    "IPedidoID IPedidoID IPedidoID IPedidoID IPedidoID IPedidoID IPedidoID IPedidoID IPedidoID IPedidoID IPedidoID IPedidoID"
    "ICliente ICliente ICliente ICliente ICliente ICliente ICliente ICliente ICliente ICliente ICliente ICliente"
    "IServicioBtn IServicioBtn IServicioBtn IServicioAcc IServicioAcc IServicioAcc IServicioAcc IServicioAcc IServicioAcc IServicioAcc IServicioAcc IServicioAcc"
    "INickname INickname INickname INickname INickname INickname INickname INickname INickname INickname INickname INickname"
    "IVendedor IVendedor IVendedor IVendedor IVendedor IVendedor IVendedor IVendedor IVendedor IVendedor IVendedor IVendedor"
    "IFechaInicial IFechaInicial IFechaInicial IFechaInicial IFechaInicial IFechaInicial IFechaInicial IFechaInicial IFechaInicial IFechaInicial IFechaInicial IFechaInicial"
    "IFechaFinal IFechaFinal IFechaFinal IFechaFinal IFechaFinal IFechaFinal IFechaFinal IFechaFinal IFechaFinal IFechaFinal IFechaFinal IFechaFinal"
    "IComentario IComentario IComentario IComentario IComentario IComentario IComentario IComentario IComentario IComentario IComentario IComentario"
  `,
    gap: "12px",
  },
}))
const IPedidoIDTratamiento = styled(Box)({
  gridArea: "IPedidoID",
})
const IServicioCTratamientoButton = styled(Box)({
  gridArea: "IServicioBtn",
})
const IServicioCTratamientoAccordion = styled(Box)({
  gridArea: "IServicioAcc",
})
const IClienteTratamiento = styled(Box)({
  gridArea: "ICliente",
})
const INicknameTratamiento = styled(Box)({
  gridArea: "INickname",
})
const IVendedorTratamiento = styled(Box)({
  gridArea: "IVendedor",
})
const IFechaInicialTratamiento = styled(Box)({
  gridArea: "IFechaInicial",
})
const IFechaFinalTratamiento = styled(Box)({
  gridArea: "IFechaFinal",
})
const IComentarioTratamiento = styled(Box)({
  gridArea: "IComentario",
})

const PantallaCitaTratamiento = forwardRef(
  ({ dataInfoPednumped, dataInfoVendedor, refetchGetAllInfoFormulario }, ref) => {
    const [formData, setFormData] = useState({
      pednumped: "",
      servicios: [],
      cliente: null,
      nickname: "",
      vennombre: "",
      vencodigo: "",
      pedfecemi: dayjs(new Date()),
      pedfecven: dayjs(new Date()),
      comentario: "",
    })
    const handleSetFormData = (k, v) => setFormData((prev) => ({ ...prev, [k]: v }))

    // Modal Ayuda Servicios
    const [openModalServicios, setOpenModalServicios] = useState(false)
    const handleOpenModalServicios = () => setOpenModalServicios(true)
    const handleCloseModalServicios = () => setOpenModalServicios(false)

    useEffect(() => {
      if (dataInfoPednumped) {
        setFormData((prev) => ({
          ...prev,
          pednumped: dataInfoPednumped,
        }))
      }
    }, [dataInfoPednumped])

    const {
      data: dataClientes = [],
      isError: dataClientesError,
      isFetching: isFetchingClientes,
      isLoading: isLoadingClientes,
      refetch: refetchGetAllClientes,
    } = useGetClientes()

    function useGetClientes() {
      return useQuery({
        queryKey: ["AdministradorPedidosMostradorClientes", CITAS_TYPES.TRATAMIENTO],
        queryFn: async () => {
          const response = await fetchwrapper(`/AdministradorPedidosMostrador/getAllClientes`)
          let result = await response.json()
          result = result.data
          return result
        },
        onError: () => {
          console.log("Error fetching data")
        },
      })
    }

    // Cada vez que se escoga un cliente nuevo seteo en [] los servicios, y lo sugerido que es nickname y vendedor
    // Y luego llamo a api para sugerencias
    useEffect(() => {
      handleSetFormData("servicios", [])
      handleSetFormData("nickname", "")
      handleSetFormData("vendedor", null)
      handleSetFormData("vencodigo", "")
      handleSetFormData("vennombre", "")

      if (formData.cliente && Object.keys(formData.cliente).length !== 0) {
        const getSugerenciasClienteSelected = async () => {
          const options = {
            method: "POST",
            body: JSON.stringify({ clicodigo: formData.cliente.clicodigo }),
            headers: {
              "Content-Type": "application/json",
              "Access-Control-Allow-Origin": "*",
            },
          }
          let response = await fetchwrapper(`/AdministradorPedidosMostrador/getSugerenciasClienteSelected`, options)
          response = await response.json()
          if (response.data) {
            handleSetFormData("nickname", response.data.nickname)
            handleSetFormData("vendedor", {
              value: response.data.vencodigo,
              label: `${response.data.vennombre} [${response.data.vencodigo}]`,
              vencodigo: response.data.vencodigo,
              vennombre: response.data.vennombre,
            })
            handleSetFormData("vencodigo", response.data.vencodigo)
            handleSetFormData("vennombre", response.data.vennombre)
          }
        }
        try {
          getSugerenciasClienteSelected()
        } catch (error) {
          console.error(error)
        }
      }
    }, [formData.cliente])

    const handleRemoveServicio = (objetoAEliminar) => {
      // Filtrar el arreglo para excluir el objeto que deseas eliminar
      const arregloFiltrado = formData.servicios.filter((objeto) => {
        // Comparar todas las propiedades del objeto
        return !(
          objeto.sesnumses === objetoAEliminar.sesnumses &&
          objeto.sessecuen === objetoAEliminar.sessecuen &&
          objeto.artcodigo === objetoAEliminar.artcodigo
        )
      })

      handleSetFormData("servicios", arregloFiltrado)
    }

    const { mutateAsync: savePedido, isPending: isSavingPedido } = useSavePedido()

    function useSavePedido() {
      return useMutation({
        mutationFn: async (cita) => {
          const options = {
            method: "POST",
            body: JSON.stringify({
              cabeceraCita: cita.cabeceraCita,
              detalleCita: cita.detalleCita,
            }),
            headers: {
              "Content-Type": "application/json",
              "Access-Control-Allow-Origin": "*",
            },
          }

          let response = await fetchwrapper(`/AdministradorPedidosMostrador/saveCitaTratamiento`, options)

          response = response.json()
          return response
        },
        onError: () => {
          alert("No se pudo hacer el pedido. Intente otra vez")
        },
        onSuccess: () => {
          alert("Pedido realizada con éxito")
        },
      })
    }

    const handleCreating = async () => {
      // if (!formData.pedfecemi.isValid()) {
      //   return alert("Digite una fecha de emisión válida")
      // }
      // if (!formData.pedfecven.isValid()) {
      //   return alert("Digite una fecha de vencimiento válida")
      // }

      if (!formData.cliente || Object.keys(formData.cliente) === 0) {
        return alert("Digite un cliente")
      }
      if (formData.servicios.length === 0) {
        return alert("Seleccione al menos un servicio")
      }

      if (!formData.vendedor || Object.keys(formData.vendedor || {}).length === 0) {
        return alert("Digite un vendedor")
      }

      if (!formData.nickname || formData.nickname === "") {
        return alert("Digite un nombre")
      }

      console.log({
        cabeceraCita: {
          pednumped: formData.pednumped,
          clicodigo: formData.cliente.clicodigo,
          clinombre: formData.cliente.clinombre,
          nickname: formData.nickname,
          vennombre: formData.vennombre,
          vencodigo: formData.vencodigo,
          pedfecemi: formData.pedfecemi,
          pedfecven: formData.pedfecven,
          comentario: formData.comentario,
        },
        detalleCita: formData.servicios,
      })

      await savePedido({
        cabeceraCita: {
          pednumped: formData.pednumped,
          clicodigo: formData.cliente.clicodigo,
          clinombre: formData.cliente.clinombre,
          nickname: formData.nickname.trim(),
          vennombre: formData.vennombre,
          vencodigo: formData.vencodigo,
          pedfecemi: formData.pedfecemi.format("YYYY-MM-DDTHH:mm:ss.SSS") + "Z",
          pedfecven: formData.pedfecven.format("YYYY-MM-DDTHH:mm:ss.SSS") + "Z",
          comentario: formData.comentario.trim(),
        },
        detalleCita: formData.servicios,
      })

      // Limpiar toda la pantalla
      setFormData({
        pednumped: "",
        servicios: [],
        cliente: null,
        nickname: "",
        vennombre: "",
        vencodigo: "",
        pedfecemi: dayjs(new Date()),
        pedfecven: dayjs(new Date()),
        comentario: "",
      })
      await refetchGetAllInfoFormulario()
    }

    // Expone la función al padre
    useImperativeHandle(ref, () => ({
      save: handleCreating,
    }))

    return (
      <>
        <CustomBackdrop isLoading={isLoadingClientes || isFetchingClientes || isSavingPedido} />
        <ContainerFormTratamiento>
          <IPedidoIDTratamiento>
            <CustomTextFieldReadable
              label="Pedido ID"
              value={formData.pednumped}
              sxInput={{
                backgroundColor: "#E0CB79",
              }}
            ></CustomTextFieldReadable>
          </IPedidoIDTratamiento>
          <IServicioCTratamientoButton>
            {formData.cliente?.value ? (
              <Button fullWidth variant="contained" color="primary" onClick={handleOpenModalServicios}>
                Servicios
              </Button>
            ) : (
              <Button fullWidth variant="contained" color="primary" disabled onClick={handleOpenModalServicios}>
                Servicios
              </Button>
            )}
            <Modal
              open={openModalServicios}
              onClose={handleCloseModalServicios}
              aria-labelledby="modal-modal-title"
              aria-describedby="modal-modal-description"
            >
              <Box sx={style.Modal}>
                <IconButton style={style.CloseIcon} onClick={() => handleCloseModalServicios()}>
                  <CloseIcon />
                </IconButton>
                <CustomSingleRowSelectionServerSide
                  endpoint={`/AdministradorPedidosMostrador/getAllServiciosxCliente`}
                  endpointJson={{ clicodigo: formData.cliente?.value }}
                  errorMsgFilterSearch="Error en cargar datos"
                  queryKeyModal="AdministradorPedidosMostrador/getAllServiciosxCliente"
                  perPage={10}
                  onHandleSelectedData={(allServicesSelected) => {
                    // Correcion 25/03/2025
                    // Antes era un multiselect pero ahora es un single, pero el estado lo seguire
                    // manejando como un arreglo que siempre tendra como maximo 1 elemento

                    // // Concatenar los dos arreglos
                    // const arregloCombinado = formData.servicios.concat(allServicesSelected)

                    // // Crear un Set para almacenar los objetos únicos
                    // const setUnico = new Set()

                    // // Filtrar los duplicados
                    // const arregloSinDuplicados = arregloCombinado.filter((objeto) => {
                    //   // Crear una clave única para cada objeto
                    //   const claveUnica = `${objeto.sesnumses}-${objeto.sessecuen}-${objeto.artcodigo}`

                    //   // Si la clave no está en el Set, agregarla y devolver true para incluir el objeto en el resultado
                    //   if (!setUnico.has(claveUnica)) {
                    //     setUnico.add(claveUnica)
                    //     return true
                    //   }

                    //   // Si la clave ya está en el Set, devolver false para excluir el objeto
                    //   return false
                    // })
                    // handleSetFormData("servicios", arregloSinDuplicados)
                    handleSetFormData("servicios", [allServicesSelected])
                    handleCloseModalServicios()
                  }}
                  columnsTable={[
                    {
                      accessorKey: "sesnumses",
                      header: "Sesión",
                      size: 150,
                      Cell: ({ cell }) => <span>{cell.getValue()}</span>,
                    },
                    {
                      accessorKey: "sessecuen",
                      header: "Num de sesión",
                      size: 150,
                      Cell: ({ cell }) => <span>{cell.getValue()}</span>,
                    },
                    {
                      accessorKey: "sescomentario",
                      header: "Num de sesión",
                      size: 300,
                      Cell: ({ cell }) => <span>{cell.getValue()}</span>,
                    },
                    {
                      accessorKey: "artcodigo",
                      header: "Código Artículo",
                      size: 150,
                      Cell: ({ cell }) => <span>{cell.getValue()}</span>,
                    },
                    {
                      accessorKey: "artdescri",
                      header: "Descripción artículo",
                      size: 200,
                      Cell: ({ cell }) => <span>{cell.getValue()}</span>,
                    },
                    {
                      accessorKey: "fecprogramada",
                      header: "Fecha Programada",
                      size: 150,
                      Cell: ({ cell }) => {
                        const value = cell.getValue()
                        return <span>{normalFormatDate(value)}</span> // Formatear la fecha
                      },
                    },
                  ]}
                />
              </Box>
            </Modal>
          </IServicioCTratamientoButton>
          <IServicioCTratamientoAccordion>
            <FloatingAccordionLike
              items={formData.servicios}
              title="Ver / Ocultar Servicios"
              onRemoveItem={handleRemoveServicio}
            />
          </IServicioCTratamientoAccordion>
          <IClienteTratamiento>
            <CustomAutocomplete
              label="Cliente"
              selectedOption={formData.cliente}
              setSelectedOption={(v) => handleSetFormData("cliente", v)}
              options={dataClientes}
            />
          </IClienteTratamiento>
          <INicknameTratamiento>
            <CustomTextField
              label="Nickname"
              value={formData.nickname}
              setValue={(v) => handleSetFormData("nickname", v)}
            ></CustomTextField>
          </INicknameTratamiento>
          <IVendedorTratamiento>
            <CustomAutocomplete
              label="Vendedor"
              selectedOption={formData.vendedor}
              setSelectedOption={(v) => {
                setFormData((prev) => ({
                  ...prev,
                  vendedor: v,
                  vencodigo: v?.vencodigo ?? null,
                  vennombre: v?.vennombre ?? null,
                }))
              }}
              options={dataInfoVendedor}
            />
          </IVendedorTratamiento>
          <IFechaInicialTratamiento>
            <CustomDatePicker
              label="Fecha Emisión"
              value={formData.pedfecemi}
              setValue={(v) => handleSetFormData("pedfecemi", v)}
              slotProps={{ textField: { fullWidth: true } }}
              disabled
            />
          </IFechaInicialTratamiento>
          <IFechaFinalTratamiento>
            <CustomDatePicker
              label="Fecha Vencimiento"
              value={formData.pedfecven}
              setValue={(v) => handleSetFormData("pedfecven", v)}
              slotProps={{ textField: { fullWidth: true } }}
              disabled
            />
          </IFechaFinalTratamiento>
          <IComentarioTratamiento>
            <CustomTextField
              label="Comentario"
              value={formData.comentario}
              setValue={(v) => handleSetFormData("comentario", v)}
            ></CustomTextField>
          </IComentarioTratamiento>
        </ContainerFormTratamiento>
      </>
    )
  },
)

// ************************************************************
//                 PANTALLA CITA AGENDA
// ************************************************************
const ContainerFormAgenda = styled(Box)(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "repeat(12, 1fr)",
  gridTemplateRows: "auto auto auto",
  gridTemplateAreas: `
          "IPedidoID IPedidoID IPedidoID IPedidoID IAgenda IAgenda IAgenda IAgenda IAgenda IAgenda IAgenda IAgenda"
          "ICliente ICliente ICliente ICliente ICliente ICliente IVendedor IVendedor IVendedor IVendedor IVendedor IVendedor"
          "IFechaInicial IFechaInicial IFechaInicial IFechaInicial IFechaInicial IFechaInicial IFechaFinal IFechaFinal IFechaFinal IFechaFinal IFechaFinal IFechaFinal"
          "IComentario IComentario IComentario IComentario IComentario IComentario IComentario IComentario IComentario IComentario IComentario IComentario"
        `,
  gap: "8px",
  alignItems: "center",
}))
const IPedidoIDAgenda = styled(Box)({
  gridArea: "IPedidoID",
})
const IAgendaAgenda = styled(Box)({
  gridArea: "IAgenda",
})
const IClienteAgenda = styled(Box)({
  gridArea: "ICliente",
})
const IVendedorAgenda = styled(Box)({
  gridArea: "IVendedor",
})
const IFechaInicialAgenda = styled(Box)({
  gridArea: "IFechaInicial",
})
const IFechaFinalAgenda = styled(Box)({
  gridArea: "IFechaFinal",
})
const IComentarioAgenda = styled(Box)({
  gridArea: "IComentario",
})
const PantallaCitaAgenda = forwardRef(({ dataInfoPednumped, dataInfoVendedor, refetchGetAllInfoFormulario }, ref) => {
  const [formData, setFormData] = useState({
    pednumped: "",
    servicios: [],
    clinombre: "",
    clicodigo: "",
    vennombre: "",
    vencodigo: "",
    pedfecemi: dayjs(new Date()),
    pedfecven: dayjs(new Date()),
    comentario: "",
  })
  const handleSetFormData = (k, v) => setFormData((prev) => ({ ...prev, [k]: v }))

  useEffect(() => {
    if (dataInfoPednumped) {
      setFormData((prev) => ({
        ...prev,
        pednumped: dataInfoPednumped,
      }))
    }
  }, [dataInfoPednumped])

  return (
    <>
      <CustomBackdrop />
      <ContainerFormAgenda>
        <IPedidoIDAgenda>
          <CustomTextFieldReadable
            label="Pedido ID"
            value={formData.pednumped}
            sxInput={{
              backgroundColor: "#E0CB79",
            }}
          ></CustomTextFieldReadable>
        </IPedidoIDAgenda>
        <IAgendaAgenda>
          <CustomHelperDetail
            label="Agenda"
            // valueSearched={filtros.clienteBodega?.clicodigo}
            endpoint="/pickingSugerido/getClientesHelper"
            valueInputMain="clicodigo"
            valueInputSecondary="clinombre"
            idSearchField="clicodigo"
            errorMsgIdSearch="Error fetching data:"
            errorMsgFilterSearch="Error en cargar datos"
            queryKeyModal="clienteBodegaPickingSugerido"
            perPage={10}
            placeholder=""
            // onHandleSelectedData={(v) => handleSetFiltros("clienteBodega", v)}
            sxInputMain={{
              minWidth: "170px",
              maxWidth: "170px",
              marginRight: "10px",
            }}
            sxInputSecondary={{
              width: "100%",
            }}
          />
        </IAgendaAgenda>
        <IClienteAgenda>
          <CustomHelper
            label="Cliente"
            // valueSearched={filtros.factura?.facnumfac}
            endpoint="/pickingSugerido/getFacturaHelper"
            valueInputMain="facnumfac"
            idSearchField="facnumfac"
            errorMsgIdSearch="Error fetching data:"
            errorMsgFilterSearch="Error en cargar datos"
            queryKeyModal="facturaPickingSugerido"
            perPage={10}
            placeholder=""
          />
        </IClienteAgenda>
        <IVendedorAgenda>
          <CustomAutocomplete
            label="Vendedor"
            selectedOption={formData.vendedor}
            setSelectedOption={(v) => {
              setFormData((prev) => ({
                ...prev,
                vendedor: v,
                vencodigo: v?.vencodigo ?? null,
                vennombre: v?.vennombre ?? null,
              }))
            }}
            options={dataInfoVendedor}
          />
        </IVendedorAgenda>
        <IFechaInicialAgenda>
          <CustomDatePicker
            label="Fecha Emisión"
            value={formData.pedfecemi}
            setValue={(v) => handleSetFormData("pedfecemi", v)}
            slotProps={{ textField: { fullWidth: true } }}
            disabled
          />
        </IFechaInicialAgenda>
        <IFechaFinalAgenda>
          <CustomDatePicker
            label="Fecha Vencimiento"
            value={formData.pedfecven}
            setValue={(v) => handleSetFormData("pedfecven", v)}
            slotProps={{ textField: { fullWidth: true } }}
            disabled
          />
        </IFechaFinalAgenda>
        <IComentarioAgenda>
          <CustomTextField
            label="Comentario"
            value={formData.comentario}
            setValue={(v) => handleSetFormData("comentario", v)}
          ></CustomTextField>
        </IComentarioAgenda>
      </ContainerFormAgenda>
    </>
  )
})

// ************************************************************
//                 COMPONENTE FINAL
// ************************************************************

const CrearAdministradorPedidosMostrador = () => {
  const navigate = useNavigate()
  const [screenSelected, setScreenSelected] = useState(CITAS_TYPES.COUNTER)
  const [saveCitaFn, setSaveCitaFn] = useState(() => {})
  const transitions = useTransition(screenSelected, {
    from: { opacity: 0, transform: "translateX(100%)" },
    enter: { opacity: 1, transform: "translateX(0%)" },
    leave: { opacity: 0, transform: "translateX(-100%)" },
    config: { duration: 300 },
    keys: [screenSelected],
  })
  const pantallaCounterRef = useRef(null)
  const pantallaTratamientoRef = useRef(null)
  const pantallaAgendaRef = useRef(null)

  const {
    data: { data: dataInfoFormulario } = { data: null },
    isFetching: isFetchingInfoFormulario,
    isLoading: isLoadingInfoFormulario,
    refetch: refetchGetAllInfoFormulario,
    isError: isErrorDataInfoFormulario,
    error: errorDataInfoFormulario,
  } = CustomUseQuery({
    queryKey: ["AdministradorPedidosMostradorInfoFormularioALL"],
    url: "/AdministradorPedidosMostrador/getInfoFormulario",
    showError: "modal",
  })

  useEffect(() => {
    if (isErrorDataInfoFormulario && errorDataInfoFormulario) {
      notificationService.showErrorWithRequestId(errorDataInfoFormulario).then(() => {
        navigate(-1)
      })
    }
  }, [isErrorDataInfoFormulario, errorDataInfoFormulario])

  // Cada vez que se transicione a un nueva cita
  // se cargaron datos generales como el id, y vendedor
  useEffect(() => {
    refetchGetAllInfoFormulario()
  }, [pantallaCounterRef, pantallaTratamientoRef, pantallaAgendaRef])

  // Función para guardar el hijo activo
  const handleSaveCita = () => {
    switch (screenSelected) {
      case CITAS_TYPES.COUNTER:
        pantallaCounterRef.current?.save()
        break
      case CITAS_TYPES.TRATAMIENTO:
        pantallaTratamientoRef.current?.save()
        break
      case CITAS_TYPES.AGENDA:
        pantallaAgendaRef.current?.save()
        break
      default:
        break
    }
  }

  return (
    <ThemeProvider theme={theme}>
      <Header />
      <div className="main main-app p-3 p-lg-4">
        <BackIcon />
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            margin: "0 30px 30px 30px",
            fontSize: "25px",
          }}
        ></div>
        <CustomBackdrop isLoading={isLoadingInfoFormulario || isFetchingInfoFormulario} />
        <Box className={StyledRoot}>
          <div style={{ display: "flex", flexDirection: "row", gap: "10px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "16px", padding: "5px" }}>
              <CustomTooltip title={"Grabar"}>
                <Button onClick={handleSaveCita}>
                  <img src={GrabarIcon} alt="Grabar" style={{ width: "40px" }} />
                </Button>
              </CustomTooltip>
            </div>
          </div>
          <br />
          <br />
          <CustomFieldset sx={{ textAlign: "center" }}>
            <FormControl>
              <FormLabel id="demo-controlled-radio-buttons-group" sx={{ fontWeight: "bolder", fontSize: "19px" }}>
                Tipo de cita
              </FormLabel>
              <RadioGroup
                row
                sx={{
                  justifyContent: "center", // Centra los elementos en el eje principal (horizontal)
                  width: "100%", // Asegura que ocupe todo el ancho disponible
                  gap: 2, // Espacio entre los radio buttons (opcional)
                }}
                aria-labelledby="demo-controlled-radio-buttons-group"
                name="controlled-radio-buttons-group"
                value={screenSelected}
                onChange={(event) => setScreenSelected(event.target.value)}
              >
                <FormControlLabel
                  value={CITAS_TYPES.COUNTER}
                  control={<Radio />}
                  label={capitalize(CITAS_TYPES.COUNTER)}
                />
                <FormControlLabel
                  value={CITAS_TYPES.TRATAMIENTO}
                  control={<Radio />}
                  label={capitalize(CITAS_TYPES.TRATAMIENTO)}
                />
                <FormControlLabel
                  value={CITAS_TYPES.AGENDA}
                  control={<Radio />}
                  label={capitalize(CITAS_TYPES.AGENDA)}
                />
              </RadioGroup>
            </FormControl>
          </CustomFieldset>
          <br />
          <br />
          <div style={{ position: "relative" }}>
            {transitions(
              (style, item) =>
                item === CITAS_TYPES.COUNTER ? (
                  <animated.div
                    style={{
                      ...style,
                      position: "absolute",
                      width: "100%",
                      top: 0,
                      left: 0,
                    }}
                  >
                    <CustomFieldset
                      title={`Formulario - ${capitalize(CITAS_TYPES.COUNTER)}`}
                      sx={{ marginBottom: "15px" }}
                    >
                      <PantallaCitaCounter
                        ref={pantallaCounterRef}
                        dataInfoPednumped={dataInfoFormulario?.pednumped || ""}
                        dataInfoVendedor={dataInfoFormulario?.vendedores || []}
                        refetchGetAllInfoFormulario={refetchGetAllInfoFormulario}
                      />
                    </CustomFieldset>
                  </animated.div>
                ) : item === CITAS_TYPES.TRATAMIENTO ? (
                  <animated.div
                    style={{
                      ...style,
                      position: "absolute",
                      width: "100%",
                      top: 0,
                      left: 0,
                    }}
                  >
                    <CustomFieldset
                      title={`Formulario - ${capitalize(CITAS_TYPES.TRATAMIENTO)}`}
                      sx={{ marginBottom: "15px" }}
                    >
                      <PantallaCitaTratamiento
                        ref={pantallaTratamientoRef}
                        dataInfoPednumped={dataInfoFormulario?.pednumped || ""}
                        dataInfoVendedor={dataInfoFormulario?.vendedores || []}
                        refetchGetAllInfoFormulario={refetchGetAllInfoFormulario}
                      />
                    </CustomFieldset>
                  </animated.div>
                ) : item === CITAS_TYPES.AGENDA ? (
                  <animated.div
                    style={{
                      ...style,
                      position: "absolute",
                      width: "100%",
                      top: 0,
                      left: 0,
                    }}
                  >
                    <CustomFieldset
                      title={`Formulario - ${capitalize(CITAS_TYPES.AGENDA)}`}
                      sx={{ marginBottom: "15px" }}
                    >
                      <PantallaCitaAgenda
                        ref={pantallaAgendaRef}
                        dataInfoPednumped={dataInfoFormulario?.pednumped || ""}
                        dataInfoVendedor={dataInfoFormulario?.vendedores || []}
                        refetchGetAllInfoFormulario={refetchGetAllInfoFormulario}
                      />
                    </CustomFieldset>
                  </animated.div>
                ) : null, // Si no hay un valor que coincida, no se renderiza nada
            )}
          </div>
        </Box>
      </div>
    </ThemeProvider>
  )
}

export default CrearAdministradorPedidosMostrador
